import { Text, View } from "react-native";
import {
  CardSurface,
  MetaText,
  Row,
  SupportingSubtitleText,
  semanticColors,
} from "@louise/moon-ui";
import { useTranslations } from "@src/packages/i18n";
import { useLocale } from "@src/packages/i18n/useLocale";

type Props = {
  /** Transfer or insemination date. Without it the milestones are skipped. */
  transferDate: string | null;
};

/**
 * The subtitle that sits under "Such wonderful news". It says "here's what's
 * next" only when there are dated milestones below it to read.
 */
export function pregnancyNextStepsSubtitleKey(
  transferDate: string | null,
): string {
  return transferDate
    ? "care.beta_result.take_a_moment_heres_whats_next"
    : "care.beta_result.positive_subhead";
}

// Beta hCG is typically ~14 days post-transfer, equivalent to ~4 weeks
// gestational age (gestational age is conventionally counted from 2 weeks
// before the transfer due to LMP math). So:
//   - first ultrasound: ~6 weeks gestational ≈ transfer + 28 days
//   - end of first trimester: ~13 weeks ≈ transfer + 77 days
function addDays(iso: string, days: number): string | null {
  const d = new Date(iso + "T00:00:00");
  if (isNaN(d.getTime())) return null;
  d.setDate(d.getDate() + days);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function formatLong(iso: string | null, locale: string): string | null {
  if (!iso) return null;
  const d = new Date(iso + "T00:00:00");
  if (isNaN(d.getTime())) return null;
  return new Intl.DateTimeFormat(locale, {
    day: "numeric",
    month: "short",
    year: "numeric",
  }).format(d);
}

/**
 * What a confirmed pregnancy leads to: the next two milestones dated from the
 * transfer, and a note that the cycle stays open for delivery details. The
 * dialog's own title and subtitle carry the greeting; this is the body under
 * them, shared by the beta result and end cycle confirmations.
 */
export function PregnancyNextSteps({ transferDate }: Props) {
  const t = useTranslations();
  const { locale } = useLocale();
  const milestones = transferDate
    ? [
        {
          label: t("care.beta_result.milestone_first_ultrasound"),
          hint: t("care.beta_result.milestone_first_ultrasound_hint"),
          date: formatLong(addDays(transferDate, 28), locale),
        },
        {
          label: t("care.beta_result.milestone_first_trimester_end"),
          hint: t("care.beta_result.milestone_first_trimester_end_hint"),
          date: formatLong(addDays(transferDate, 77), locale),
        },
      ]
    : [];

  return (
    <View style={{ gap: 16 }}>
      {milestones.length > 0 ? (
        <CardSurface style={{ paddingVertical: 4 }}>
          {milestones.map((milestone) => (
            <Row
              key={milestone.label}
              surface="plain"
              title={milestone.label}
              subtitle={milestone.hint}
              trailing={
                milestone.date ? <MetaText>{milestone.date}</MetaText> : null
              }
            />
          ))}
        </CardSurface>
      ) : null}

      <SupportingSubtitleText style={{ textAlign: "center" }}>
        <Text
          style={{ fontWeight: "600", color: semanticColors.text.secondary }}
        >
          {t("care.beta_result.keep_cycle_title")}
        </Text>{" "}
        {t("care.beta_result.keep_cycle_body")}
      </SupportingSubtitleText>
    </View>
  );
}

export default PregnancyNextSteps;
